/**
 * Tokens de design da Venove.
 *
 * **Os nomes canônicos são os semânticos de `docs/04-design.md`** (`brand`, `surface1`,
 * `danger`…), não os do protótipo (`--pink`, `--s1`). O nome diz o papel, não a cor: o dia em
 * que o rosa mudar, `brand` continua certo e `--pink` viraria mentira.
 *
 * Os valores são os do `:root` de `reference/prototipo-venove.html` (v7.2), copiados sem
 * alteração. Para portar CSS do protótipo, use `PROTOTYPE_ALIAS`, que traduz cada variável
 * antiga no token novo.
 *
 * Tema único escuro. Não existe variante clara.
 */

export const color = {
  // ----------------------------------------------------------------- Superfícies
  /** Fundo do player e das telas. */
  bg: '#000000',
  /**
   * Área em volta do vídeo 9:16 quando ele é centralizado em tela larga (≥ 768 px).
   * No protótipo é `--page`, o fundo fora da moldura do celular.
   */
  backdrop: '#0a0a0b',
  /** Cartões e grupos de ajustes. */
  surface1: '#121214',
  /** Campos, chips, opções de plano. */
  surface2: '#1c1c1f',
  /** Botão secundário, trilhas de progresso. */
  surface3: '#2a2a2e',

  // ----------------------------------------------------------------- Linhas
  /** Divisórias. */
  line: 'rgba(255,255,255,.10)',
  /** Divisória de mais contraste (borda de campo, contorno de cartão selecionável). */
  lineStrong: 'rgba(255,255,255,.18)',

  // ----------------------------------------------------------------- Texto
  /** Texto principal. */
  text: '#ffffff',
  /** Texto secundário. */
  muted: 'rgba(255,255,255,.62)',
  /** Texto terciário e títulos de seção. */
  dim: 'rgba(255,255,255,.50)',

  // ----------------------------------------------------------------- Marca
  /** Rosa Venove: ação principal, curtida, destaque. Um por tela. */
  brand: '#ff2e63',
  /** Fundo de selo pequeno com texto branco (≤ 13 px) — 4,6:1. */
  brandDeep: '#e5134d',
  /** Fundo de destaque. */
  brandSoft: 'rgba(255,46,99,.16)',

  // ----------------------------------------------------------------- Estados
  /** Disponível, aprovado, assistido. */
  ok: '#34c77b',
  /** Versão do `ok` legível como texto sobre fundo escuro. */
  okText: '#5fe0a0',
  /**
   * Atenção: cadeado de episódio bloqueado, item salvo na Minha lista e selo `.pill.warn`.
   * No protótipo é `--gold`.
   */
  warn: '#ffc83d',
  /** Versão fechada do `warn`, para fundo de selo. No protótipo é `--gold-deep`. */
  warnDeep: '#f59e0b',
  /** Botão destrutivo. */
  danger: '#e5484d',
  /** Texto de erro e de ação destrutiva. */
  dangerText: '#ff6b6b',

  // ----------------------------------------------------------------- Canais e selos
  /** Só no fluxo Pix. */
  pix: '#32bcad',
  /** Só no botão de compartilhar no WhatsApp. */
  whatsapp: '#25d366',
  /** Selo de classificação 14 anos. */
  age14: '#f08a24',
} as const;

export type ColorToken = keyof typeof color;

/**
 * Variável do protótipo → token canônico.
 *
 * Serve para portar o CSS de `reference/prototipo-venove.html` sem adivinhação: procure
 * `var(--pink)` no protótipo, veja aqui que é `brand`, use `color.brand`.
 */
export const PROTOTYPE_ALIAS = {
  '--bg': 'bg',
  '--page': 'backdrop',
  '--s1': 'surface1',
  '--s2': 'surface2',
  '--s3': 'surface3',
  '--line': 'line',
  '--line2': 'lineStrong',
  '--text': 'text',
  '--muted': 'muted',
  '--dim': 'dim',
  '--pink': 'brand',
  '--pink-deep': 'brandDeep',
  '--pink-soft': 'brandSoft',
  '--gold': 'warn',
  '--gold-deep': 'warnDeep',
  '--ok': 'ok',
  '--ok-text': 'okText',
  '--danger': 'danger',
  '--danger-text': 'dangerText',
  '--pix': 'pix',
  '--wa': 'whatsapp',
  '--age14': 'age14',
} as const satisfies Record<string, ColorToken>;

/** Nome da variável CSS de cada token, em kebab-case, como `docs/04` escreve. */
export function cssVarName(token: ColorToken): string {
  return `--${token.replace(/([a-z])([A-Z0-9])/g, '$1-$2').toLowerCase()}`;
}

/**
 * Declarações para o `:root` da página, com os nomes semânticos.
 *
 * Usada pela casca HTML para que o CSS escrito à mão (player, folhas) fale a mesma língua dos
 * componentes, em vez de repetir valores hexadecimais soltos.
 */
export function cssRootVariables(): string {
  return (Object.keys(color) as ColorToken[])
    .map((token) => `  ${cssVarName(token)}: ${color[token]};`)
    .join('\n');
}

/** Escala de espaçamento. Só estes valores. Nada de 3, 5, 6, 7, 9, 10, 11, 13, 14 px. */
export const space = { sp1: 4, sp2: 8, sp3: 12, sp4: 16, sp5: 24, sp6: 32 } as const;

/** Margem lateral padrão das telas. */
export const GUTTER = space.sp4;

export const radius = {
  /** Selos, chips do player, miniaturas, botão pequeno. */
  sm: 8,
  /** Botões, campos, capas, código Pix. */
  md: 12,
  /** Cartões, planos, grupos, folhas. */
  lg: 16,
  /** Chips, selos, barras de progresso. */
  pill: 999,
} as const;

export const font = {
  ui: '"Poppins", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  code: 'ui-monospace, SFMono-Regular, Menlo, monospace',
} as const;

/**
 * Escala tipográfica. Sem meios-pixels. Logotipos ficam fora da escala.
 * `display` e `head17` aparecem como literais no protótipo; estão aqui para não virarem
 * número solto no código.
 */
export const type = {
  /** Título do anúncio, valor do Pix. */
  display: { size: 32, weight: 800 },
  /** Título de aba, título da tela de sucesso. */
  screen: { size: 24, weight: 700 },
  /** Título de folha, nome no perfil, cartão do passe, preço do plano. */
  title: { size: 20, weight: 700 },
  /** Título de seção de conteúdo, título da série no vídeo, legenda do vídeo. */
  head17: { size: 17, weight: 600 },
  /** Título da barra de subtela. */
  head: { size: 16, weight: 600 },
  /** Linha de lista e corpo. */
  body: { size: 15, weight: 500 },
  /** Lead, subtítulo de folha, chips, botão médio e pequeno. */
  support: { size: 14, weight: 400 },
  /** Subtítulo, ajuda, rótulo de campo. */
  sub: { size: 13, weight: 400 },
  /** CAIXA ALTA espaçada (.08em): título de seção, contadores. */
  label: { size: 12, weight: 600 },
  /** Selo e rótulo da barra de abas. */
  badge: { size: 11, weight: 600 },
} as const;

/** Alturas de botão. O alvo de toque nunca é menor que `MIN_TOUCH`. */
export const button = { lg: 48, md: 44, sm: 36 } as const;

/** Todo alvo tem no mínimo 44×44. Quando o desenho é menor, a área cresce com `::after`. */
export const MIN_TOUCH = 44;

/** Larguras de referência da auditoria de encaixe (`docs/04`). */
export const BREAKPOINTS = [320, 360, 390, 430, 768, 1280, 1440] as const;

/** Acima disso o vídeo 9:16 centraliza e as telas de ajustes limitam a largura. */
export const WIDE_FROM = 768;
/** Largura máxima das telas de perfil e ajustes em tela grande. */
export const SETTINGS_MAX_WIDTH = 560;

export const motion = {
  /** Troca de vídeo por deslize, com mola. */
  swipe: { duration: 380, easing: 'cubic-bezier(.2,.7,.2,1)' },
  /** Folhas sobem de baixo. */
  sheet: { duration: 300, easing: 'cubic-bezier(.2,.7,.2,1)' },
  /** Toast some sozinho. */
  toast: { duration: 2600 },
} as const;
